import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  deDE,
} from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { IOffer } from '../../../interfaces/IOffer';
import { IOfferPosition } from '../../../interfaces/IOfferPosition';
import { IOfferState } from '../../../interfaces/IOfferState';
import { IOfferType } from '../../../interfaces/IOfferType';
import { useConfirmation } from '../../context/ConfirmationContext';

interface IProps {
  defaultPartnerId: number | null;
  offers: IOffer[];
  offerStates: IOfferState[];
  offerTypes: IOfferType[];
  setPositions: React.Dispatch<React.SetStateAction<IOfferPosition[]>>;
  unmount: () => void;
}

export const AddOfferPositionsDialog: React.FC<IProps> = ({
  defaultPartnerId,
  offers,
  offerStates,
  offerTypes,
  setPositions,
  unmount,
}) => {
  const { showConfirmation } = useConfirmation();
  const [selection, setSelection] = useState<GridRowId[]>([]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Nr.',
        width: 70,
      },
      {
        field: 'OfferType',
        headerName: 'Art',
        renderCell: ({ value }: GridRenderCellParams<IOffer, number>) =>
          offerTypes.find(x => value === x.id)?.offerType,
        width: 200,
      },
      {
        field: 'OfferState',
        headerName: 'Status',
        renderCell: ({ value }: GridRenderCellParams<IOffer, number>) =>
          offerStates.find(x => value === x.id)?.offerState,
        width: 150,
      },
      {
        field: 'title',
        headerName: 'Titel',
        flex: 1,
      },
    ],
    [offerTypes, offerStates]
  );

  const onSubmit = useCallback(async () => {
    const newPositions = selection
      .map(oId => offers.find(x => x.id === oId))
      .filter((o): o is IOffer => o !== undefined)
      .flatMap(o => o.OfferPositions)
      .map(p => ({ ...p, Partner: defaultPartnerId }));

    if (newPositions.length === 0) {
      await showConfirmation({
        title: 'Angebot beinhaltet keine Positionen',
        message:
          'Die ausgewählten Angebote beinhalten keine Positionen welche hinzugefügt werden können. Bitte wählen Sie andere Angebote aus oder fügen Sie positionen den ausgewählten Angeboten hinzu.',
        confirmButtonText: 'Ok',
      });
      return;
    }

    setPositions(prev => {
      const positionsMap = new Map(newPositions.map(p => [p.id, p]));

      prev.forEach(p => {
        positionsMap.set(p.id, p);
      });

      return Array.from(positionsMap.values());
    });
    unmount();
  }, [defaultPartnerId, selection, offers, setPositions, unmount]);

  return (
    <Dialog open={true} onClose={unmount} maxWidth='md' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Positionen aus folgenden Angeboten hinzufügen
        <IconButton onClick={unmount} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DataGrid
          sx={{ mt: 2 }}
          autoHeight
          checkboxSelection
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={offers}
          onRowSelectionModelChange={newRowSelectionModel =>
            setSelection([...newRowSelectionModel])
          }
          rowSelectionModel={selection}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={unmount}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={onSubmit}
          disabled={selection.length === 0}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
